// ABOUTME: Top toolbar component with navigation and user menu.
// ABOUTME: Displays app title, breadcrumbs, and user actions.

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Pin, Settings, LogOut, User, Home, MessageSquare, FileText, ChevronDown, Sun, Moon, TrendingUp, Database } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { useTheme } from '@/context/ThemeContext';
import { useTour } from '@/context/TourContext';
import { Button } from '@/components/ui/Button';
import { NotificationBadge } from '@/components/layout/NotificationBadge';
import { WorkspaceSwitcher } from '@/components/workspace/WorkspaceSwitcher';
import { billingApi } from '@/services/api/billingClient';
import { formatTierName } from '@/utils/billing';
import type { UsageSummary } from '@/types/api';

interface ToolbarProps {
  showPinboardToggle?: boolean;
}

/**
 * Compute the highest usage percentage across all resources.
 * Returns a value from 0 to 1.
 */
function getMaxUsagePercent(usage: UsageSummary['usage']): number {
  const resources = Object.values(usage);
  let max = 0;
  for (const r of resources) {
    if (r.limit > 0) {
      max = Math.max(max, r.used / r.limit);
    }
  }
  return max;
}

/**
 * Determine the avatar badge color based on usage percentage.
 * Returns null if no badge should be shown (< 70%).
 */
function getUsageBadgeColor(maxPercent: number): string | null {
  if (maxPercent >= 0.9) return 'bg-red-500';
  if (maxPercent >= 0.7) return 'bg-yellow-400';
  return null;
}

/**
 * Find the resource with the highest usage percentage.
 */
function getTopResource(usage: UsageSummary['usage']): { name: string; used: number; limit: number; percent: number } | null {
  let topName = '';
  let topPercent = 0;
  let topUsed = 0;
  let topLimit = 0;
  for (const [name, r] of Object.entries(usage)) {
    if (r.limit > 0) {
      const percent = r.used / r.limit;
      if (percent > topPercent) {
        topPercent = percent;
        topName = name;
        topUsed = r.used;
        topLimit = r.limit;
      }
    }
  }
  if (!topName) return null;
  // Format resource name for display
  const displayName = topName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return { name: displayName, used: topUsed, limit: topLimit, percent: topPercent };
}

const USAGE_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function Toolbar({ showPinboardToggle = true }: ToolbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { pinnedItems, togglePinboard, isPinboardOpen, isSelfService, isFreeTier, currentWorkspaceId, availableWorkspaces, selfServiceDataConfigEnabled } = useApp();
  const { toggleTheme, isDark } = useTheme();
  const { isRunning: isTourRunning, currentAction, onReportsNavClicked } = useTour();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Usage data for self-service users (badge + summary)
  const [usageData, setUsageData] = useState<UsageSummary | null>(null);

  const fetchUsage = useCallback(async () => {
    if (!isSelfService) return;
    try {
      const data = await billingApi.getUsage();
      setUsageData(data);
    } catch (err) {
      console.error('[Toolbar] Failed to fetch usage:', err);
    }
  }, [isSelfService]);

  // Fetch usage on mount and periodically for self-service users
  useEffect(() => {
    if (!isSelfService) return;
    fetchUsage();
    const interval = setInterval(fetchUsage, USAGE_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [isSelfService, fetchUsage]);

  // Derive badge color from usage data
  const maxPercent = usageData ? getMaxUsagePercent(usageData.usage) : 0;
  const badgeColor = isSelfService ? getUsageBadgeColor(maxPercent) : null;

  // Get current workspace tier for display
  const currentTier = availableWorkspaces.find(
    w => w.workspace_id === currentWorkspaceId
  )?.subscription_tier ?? '';
  const topResource = usageData ? getTopResource(usageData.usage) : null;

  const isHome = location.pathname === '/';
  const isDataSources = location.pathname.startsWith('/data-sources');
  const isInvestigations = location.pathname.startsWith('/investigations');
  const isReports = location.pathname.startsWith('/reports');
  const isOnboarding = location.pathname === '/onboarding';

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
  };

  return (
    <header className="h-14 flex items-center justify-between px-4 border-b border-surface-200 bg-surface-0 relative z-30">
      {/* Left: Logo and Navigation */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 hover:opacity-80 transition-all"
        >
          <img
            src="/oleum-logo.png"
            alt="Oleum BI"
            className="w-8 h-8 object-contain"
          />
          <span className="font-semibold text-surface-800 hidden sm:inline">
            Oleum BI
          </span>
        </button>

        {/* Hide navigation on onboarding page */}
        {!isOnboarding && (
          <>
            <div className="h-5 w-px bg-surface-200" />

            {/* Navigation Tabs */}
            <nav className="flex items-center gap-1" data-tour="navigation">
              <button
                onClick={() => navigate('/')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                  isHome
                    ? 'nav-tab-active'
                    : 'nav-tab'
                }`}
              >
                <Home className="w-4 h-4" />
                <span className="hidden md:inline">Home</span>
              </button>
              {selfServiceDataConfigEnabled && (
                <button
                  onClick={() => navigate('/data-sources')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                    isDataSources
                      ? 'nav-tab-active'
                      : 'nav-tab'
                  }`}
                >
                  <Database className="w-4 h-4" />
                  <span className="hidden md:inline">Data Sources</span>
                </button>
              )}
              <button
                onClick={() => navigate('/investigations')}
                data-tour="nav-investigations"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                  isInvestigations
                    ? 'nav-tab-active'
                    : 'nav-tab'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden md:inline">Investigations</span>
              </button>
              <button
                onClick={() => {
                  // Notify tour if it's waiting for this action
                  if (isTourRunning && currentAction === 'click-reports-nav') {
                    onReportsNavClicked();
                  }
                  navigate('/reports');
                }}
                data-tour="nav-reports"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                  isReports
                    ? 'nav-tab-active'
                    : 'nav-tab'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span className="hidden md:inline">Reports</span>
              </button>
            </nav>
          </>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Notification Badge */}
        {!isOnboarding && <NotificationBadge />}

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="relative w-9 h-9 rounded-sm flex items-center justify-center hover:bg-surface-100 transition-colors overflow-hidden"
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isDark ? (
              <motion.div
                key="moon"
                initial={{ y: -20, opacity: 0, rotate: -90 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: 20, opacity: 0, rotate: 90 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Moon className="w-4 h-4 text-primary-400" />
              </motion.div>
            ) : (
              <motion.div
                key="sun"
                initial={{ y: 20, opacity: 0, rotate: 90 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: -20, opacity: 0, rotate: -90 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Sun className="w-4 h-4 text-accent-500" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>

        {/* Workspace Switcher */}
        <WorkspaceSwitcher />

        {/* Pinboard Toggle - hidden on onboarding */}
        {showPinboardToggle && !isOnboarding && (
          <Button
            variant="ghost"
            size="sm"
            onClick={togglePinboard}
            className={`relative ${isPinboardOpen ? 'text-primary-600' : ''}`}
            aria-label="Toggle pinboard"
            data-tour="pinboard-toggle"
          >
            <Pin className="w-4 h-4" />
            {pinnedItems.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {pinnedItems.length}
              </span>
            )}
          </Button>
        )}

        {/* Admin Link (if admin) */}
        {user?.role === 'admin' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            aria-label="Admin settings"
          >
            <Settings className="w-4 h-4" />
          </Button>
        )}

        {/* User Menu */}
        <div
          ref={userMenuRef}
          data-testid="user-menu"
          data-tour="user-menu"
          className="relative flex items-center gap-2 pl-2 ml-2 border-l border-surface-200"
        >
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-surface-100 transition-colors"
          >
            <div className="relative">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-7 h-7 rounded-full"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-surface-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-surface-500" />
                </div>
              )}
              {badgeColor && (
                <span
                  data-testid="usage-badge"
                  className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface-0 ${badgeColor}`}
                  aria-label="Usage indicator"
                />
              )}
            </div>
            <span className="text-sm text-surface-600 hidden md:inline">
              {user?.name || 'User'}
            </span>
            <ChevronDown className={`w-4 h-4 text-surface-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isUserMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-56 card shadow-lg py-1 z-50">
              {/* Usage Summary Block — self-service users only */}
              {isSelfService && usageData && topResource && (
                <div data-testid="usage-summary" className="px-4 py-3 bg-surface-50 border-b border-surface-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded-sm bg-primary-100 text-primary-700">
                      {formatTierName(currentTier)}
                    </span>
                  </div>
                  <div className="text-xs text-surface-600">
                    <div className="flex justify-between mb-1">
                      <span>{topResource.name}</span>
                      <span>{topResource.used}/{topResource.limit}</span>
                    </div>
                    <div className="w-full h-1.5 bg-surface-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          topResource.percent >= 0.9 ? 'bg-red-500' :
                          topResource.percent >= 0.7 ? 'bg-yellow-400' :
                          'bg-primary-500'
                        }`}
                        style={{ width: `${Math.min(topResource.percent * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Self-service menu items */}
              {isFreeTier && (
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    navigate('/upgrade');
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-surface-50 transition-colors"
                >
                  <TrendingUp className="w-4 h-4" />
                  Upgrade to Pro
                </button>
              )}
              <button
                onClick={() => {
                  setIsUserMenuOpen(false);
                  navigate('/settings/billing');
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-surface-600 hover:text-surface-800 hover:bg-surface-50 transition-colors"
              >
                <Settings className="w-4 h-4" />
                Settings & Billing
              </button>
              <div className="border-t border-surface-200 my-1" />
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-surface-600 hover:text-surface-800 hover:bg-surface-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
